import React, { useCallback, useEffect, useState } from 'react';
import { X, Pencil, StopCircle, UserMinus } from 'lucide-react';
import { domiciliaryScheduleService } from '../../../services';
import { Modal, Button, VisitLocationMap, UserPhoto } from '../../../components';
import { VisitDetailsSkeleton } from '../../../skeletons';
import { localTime } from '../../../utils/datetime';
import { toast } from 'react-hot-toast';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Read-style visit detail: status, assigned carer, task outcomes, and
// planned-vs-actual time. Footer actions fork the package from this day
// (edit forward) or stop it from this day.
const STATUS = {
  scheduled: { label: 'Scheduled', dot: 'bg-blue-500', wrap: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300', tag: 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300' },
  in_progress: { label: 'In progress', dot: 'bg-sky-500', wrap: 'bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-300', tag: 'bg-sky-100 dark:bg-sky-900/40 text-sky-600 dark:text-sky-300' },
  completed: { label: 'Done', dot: 'bg-emerald-500', wrap: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300', tag: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300' },
  missed: { label: 'Missed', dot: 'bg-rose-500', wrap: 'bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300', tag: 'bg-rose-100 dark:bg-rose-900/40 text-rose-600 dark:text-rose-300' },
  unassigned: { label: 'Unassigned', dot: 'bg-gray-400', wrap: 'bg-gray-50 dark:bg-[#31373f] text-gray-600 dark:text-gray-300', tag: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300' },
};
const TASK_LEVEL = { required: 'text-rose-500', if_needed: 'text-amber-500', optional: 'text-gray-400 dark:text-gray-500' };

// A stored UTC timestamp shown in the VIEWER's local time (e.g. "26 Jun, 13:00").
const fmtLocal = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.getDate()} ${MONTHS[d.getMonth()]}, ${localTime(iso)}`;
};
// Scheduled times are wall-clock 'HH:MM' (no timezone) — show as-is with the date.
const schedLabel = (ymd, hm) => {
  if (!ymd || !hm) return '—';
  const [, m, d] = ymd.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]}, ${hm}`;
};
const minsBetween = (a, b) => {
  if (!a || !b) return null;
  return Math.round((new Date(b).getTime() - new Date(a).getTime()) / 60000);
};
// Minutes → "45m" / "1h" / "5h 42m".
const fmtMins = (n) => {
  const x = Math.abs(Math.round(n));
  if (x < 60) return `${x}m`;
  const h = Math.floor(x / 60); const m = x % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};
const toMin = (hm) => { const [h, m] = String(hm).split(':').map(Number); return h * 60 + (m || 0); };
// Scheduled wall-clock time on `ymd` as a local Date instant (for variance).
const schedInstant = (ymd, hm) => {
  if (!ymd || !hm) return null;
  const [y, mo, d] = ymd.split('-').map(Number);
  const [h, mi] = hm.split(':').map(Number);
  return new Date(y, mo - 1, d, h, mi);
};
const TimeCell = ({ label, value }) => (
  <div className="rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2">
    <div className="text-[9px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">{label}</div>
    <div className="mt-1 text-[12px] font-semibold text-gray-700 dark:text-gray-200">{value}</div>
  </div>
);

// `stageStop(assignmentId, ctx)` / `stageUnassign(visit)` (optional) — when
// provided (Schedule screen), those footer actions queue a draft change instead
// of writing immediately.
const VisitDetailsModal = ({ visit, date, onClose, onChanged, onEditFromDay, stageStop, stageUnassign, stageHoursEdit }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Inline "Edit times" form (Schedule screen only — needs stageHoursEdit).
  // Re-seeded from the recorded times whenever the visit data (re)loads.
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ clockIn: '', clockOut: '', note: '' });
  useEffect(() => {
    setEditing(false);
    setEditForm({
      clockIn: localTime(data?.actual?.clock_in) || '',
      clockOut: localTime(data?.actual?.clock_out) || '',
      note: '',
    });
  }, [data]);

  // Named so it can be re-run after an action changes the visit (e.g. a manager
  // approving an out-of-area location), not just on open.
  const load = useCallback(({ silent = false } = {}) => {
    if (!visit) return;
    if (!silent) setLoading(true);
    domiciliaryScheduleService.getVisitDetails(visit.assignment_id, date, visit._carerId)
      .then((res) => { if (res.success) setData(res.data); })
      .catch(() => toast.error('Failed to load visit'))
      .finally(() => { if (!silent) setLoading(false); });
  }, [visit, date]);

  useEffect(() => { load(); }, [load]);

  if (!visit) return null;

  const stopFromDay = async () => {
    if (!window.confirm(`Stop this care package from ${date} onward? Past visits stay in history.`)) return;
    if (stageStop) {
      stageStop(visit.assignment_id, { name: data?.assignment?.resident?.name || data?.assignment?.package_name });
      toast.success('Stop added to draft');
      onClose();
      return;
    }
    try {
      const res = await domiciliaryScheduleService.stopFromDate({ assignment_id: visit.assignment_id, date });
      if (res.success) { toast.success(res.message); onChanged && onChanged(); onClose(); }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error stopping package');
    }
  };

  const st = STATUS[data?.status] || STATUS.scheduled;
  const a = data?.assignment;

  // Unassign is allowed any time EXCEPT inside the lock window that runs from
  // 2 hours before the start until the visit ends. e.g. a 09:00 visit locks at
  // 07:00 and reopens once it's over. Before 07:00, or after the end, it's free.
  const hasCarer = !!data?.carer;
  const now = Date.now();
  const startMs = (a && a.start) ? schedInstant(date, a.start).getTime() : null;
  let endMs = (a && a.end) ? schedInstant(date, a.end).getTime() : null;
  if (startMs != null && endMs != null && endMs < startMs) endMs += 86400000; // overnight
  const within2h = startMs != null && now > startMs - 2 * 3600000 && now < startMs;
  const during = data?.status === 'in_progress'
    || (startMs != null && endMs != null && now >= startMs && now <= endMs);
  const canUnassign = hasCarer && !within2h && !during;
  const unassignReason = within2h ? 'Locked within 2 hours of the start'
    : during ? 'Visit is in progress'
    : '';

  const unassign = async () => {
    if (!canUnassign) return;
    if (!window.confirm(`Unassign ${data?.carer?.name || 'the carer'} from this visit on ${date}?`)) return;
    if (stageUnassign) {
      stageUnassign(visit);
      toast.success('Unassign added to draft');
      onClose();
      return;
    }
    try {
      const res = await domiciliaryScheduleService.assignCarer({ assignment_id: visit.assignment_id, date, carer_ids: [], scope: 'today' });
      if (res.success) { toast.success('Carer unassigned'); onChanged && onChanged(); onClose(); }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error unassigning');
    }
  };

  // Variance: scheduled (wall-clock, as a local instant) vs actual (UTC instant).
  const schedStart = a ? schedInstant(date, a.start) : null;
  const startDiff = (data?.actual?.clock_in && schedStart)
    ? Math.round((new Date(data.actual.clock_in).getTime() - schedStart.getTime()) / 60000) : null;
  const worked = data?.actual ? minsBetween(data.actual.clock_in, data.actual.clock_out) : null;
  let plannedLen = (a && a.start && a.end) ? toMin(a.end) - toMin(a.start) : null;
  if (plannedLen != null && plannedLen < 0) plannedLen += 1440; // overnight
  const lenDiff = (worked != null && plannedLen != null) ? worked - plannedLen : null;

  const canEditHours = !!(stageHoursEdit && data?.carer && data?.actual?.clock_in && data?.actual?.clock_out);
  const changeLog = Array.isArray(data?.actual?.hours_change_log) ? data.actual.hours_change_log : [];
  const range = (r) => (r ? `${localTime(r.start) || '—'} – ${r.end ? localTime(r.end) : '…'}` : null);
  const inputCls = 'w-full rounded-lg border border-[#E2E8F0] dark:border-gray-700 bg-white dark:bg-[#31373f] dark:text-gray-200 px-3 py-2 text-[14px] outline-none focus:border-blue-500';

  return (
    <Modal isOpen onClose={onClose} hideHeader bodyClassName="flex flex-col flex-1 min-h-0" maxWidth="max-w-3xl">
        <div className="flex items-start justify-between border-b border-gray-100 dark:border-gray-800 px-6 py-4">
          <div>
            <h2 className="text-[17px] font-semibold text-gray-800 dark:text-gray-200">{a ? `${a.package_name} · ${a.start}` : 'Visit'}</h2>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {a?.resident?.name} · {date}{a?.duration ? ` · ${a.duration}m` : ''}{a?.zone_name ? ` · ${a.zone_name}` : ''}
            </p>
          </div>
          <button onClick={onClose} className="grid h-6 w-6 place-items-center rounded-full bg-blue-600 text-white hover:bg-blue-700"><X className="h-3.5 w-3.5" strokeWidth={3} /></button>
        </div>

        {loading ? (
          <VisitDetailsSkeleton />
        ) : (
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
            {/* Status banner */}
            <div className={`flex items-center justify-between rounded-xl px-4 py-3 ${st.wrap}`}>
              <span className="flex items-center gap-2 text-sm font-bold"><span className={`h-2.5 w-2.5 rounded-full ${st.dot}`} />{st.label}</span>
              <span className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase ${st.tag}`}>{data?.status}</span>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* Assigned + tasks */}
              <div className="space-y-4">
                <div>
                  <h3 className="mb-2 text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">Assigned</h3>
                  {data?.carer ? (
                    <div className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-800 p-3">
                      <UserPhoto
                        src={data.carer.avatar_url}
                        alt={data.carer.name}
                        className="h-10 w-10 rounded-full"
                        fallback={(
                          <span className="grid h-10 w-10 place-items-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700">{data.carer.initials}</span>
                        )}
                      />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-gray-800 dark:text-gray-200">{data.carer.name}</div>
                        <div className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">Carer · covers {data.carer.zone}</div>
                      </div>
                      <button
                        onClick={unassign}
                        disabled={!canUnassign}
                        title={canUnassign ? 'Remove this carer from the visit' : unassignReason}
                        className="ml-auto inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-1.5 text-[12px] font-semibold text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-300 disabled:hover:bg-transparent"
                      >
                        <UserMinus className="h-3.5 w-3.5" /> Unassign
                      </button>
                    </div>
                  ) : <div className="rounded-xl bg-rose-50/60 p-3 text-sm font-semibold text-rose-600">No carer assigned</div>}
                  {hasCarer && !canUnassign && unassignReason && (
                    <p className="mt-1 text-[10px] text-gray-400 dark:text-gray-500">{unassignReason}</p>
                  )}
                </div>

                <div>
                  <h3 className="mb-2 text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">Tasks</h3>
                  <div className="space-y-2">
                    {(data?.tasks || []).map((t, i) => (
                      <div key={i} className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2">
                        <span className={`h-2.5 w-2.5 rounded-full ${t.completed ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                        <span className={`flex-1 text-sm ${t.completed ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-gray-700 dark:text-gray-200'}`}>{t.text}</span>
                        {/* When an outcome was recorded, show the time it happened. */}
                        {localTime(t.recorded_at) && (
                          <span className="whitespace-nowrap text-[10px] font-semibold text-emerald-600">{localTime(t.recorded_at)}</span>
                        )}
                        <span className={`text-[9px] font-bold uppercase ${TASK_LEVEL[t.level] || 'text-gray-400 dark:text-gray-500'}`}>{(t.level || '').replace('_', ' ')}</span>
                      </div>
                    ))}
                    {(data?.tasks || []).length === 0 && <div className="text-xs text-gray-400 dark:text-gray-500">No tasks.</div>}
                  </div>
                </div>
              </div>

              {/* Planned & actual */}
              <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4">
                <h3 className="mb-3 text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">Planned &amp; actual time</h3>
                <div className="grid grid-cols-2 gap-2">
                  <TimeCell label="Scheduled start" value={schedLabel(date, a?.start)} />
                  <TimeCell label="Scheduled end" value={schedLabel(date, a?.end)} />
                  <TimeCell label="Actual clock-in" value={fmtLocal(data?.actual?.clock_in)} />
                  <TimeCell label="Actual clock-out" value={fmtLocal(data?.actual?.clock_out)} />
                  <TimeCell label="Worked duration" value={worked != null ? fmtMins(worked) : '—'} />
                  <TimeCell label="Break" value={fmtMins(data?.actual?.break_minutes || 0)} />
                </div>
                {data?.actual && (
                  <div className="mt-3 rounded-lg bg-emerald-50 px-3 py-2 text-[12px] font-medium text-emerald-700">
                    {[
                      startDiff != null && (startDiff === 0 ? 'Started on time' : `Started ${fmtMins(startDiff)} ${startDiff < 0 ? 'early' : 'late'}`),
                      lenDiff != null && (lenDiff === 0 ? 'exactly the planned length' : `visit ran ${fmtMins(lenDiff)} ${lenDiff < 0 ? 'shorter than planned' : 'longer than planned'}`),
                    ].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
            </div>

            {/* Edit times — any finished visit, including ones already
                approved and published. Staged as a draft; needs a reason. */}
            {canEditHours && editing && (
              <div className="space-y-3 rounded-xl border border-blue-100 dark:border-blue-900/50 bg-blue-50/40 dark:bg-blue-900/20 p-4">
                <h3 className="text-[13px] font-bold text-[#1E3A8A] dark:text-blue-300">Edit clocked times</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-[11px] font-extrabold uppercase tracking-wide text-[#475569] dark:text-gray-400">Clock-in</label>
                    <input type="time" value={editForm.clockIn} onChange={(e) => setEditForm((f) => ({ ...f, clockIn: e.target.value }))} className={inputCls} />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-extrabold uppercase tracking-wide text-[#475569] dark:text-gray-400">Clock-out</label>
                    <input type="time" value={editForm.clockOut} onChange={(e) => setEditForm((f) => ({ ...f, clockOut: e.target.value }))} className={inputCls} />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-extrabold uppercase tracking-wide text-[#475569] dark:text-gray-400">Reason for change (required)</label>
                  <textarea rows={2} value={editForm.note} onChange={(e) => setEditForm((f) => ({ ...f, note: e.target.value }))}
                    placeholder="e.g. Carer forgot to clock out — confirmed with the client's family"
                    className={`${inputCls} resize-none text-[13px]`} />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="secondary" onClick={() => setEditing(false)}>Cancel</Button>
                  <Button
                    variant="primary"
                    disabled={!editForm.clockIn || !editForm.clockOut || !editForm.note.trim()}
                    onClick={() => stageHoursEdit(
                      { visit: { assignment_id: visit.assignment_id, resident: { name: a?.resident?.name }, package_name: a?.package_name }, carer: data.carer },
                      editForm.clockIn, editForm.clockOut, editForm.note,
                    )}
                  >
                    Save change
                  </Button>
                </div>
              </div>
            )}

            {/* Change history — every approval / edit of the clocked times. */}
            {changeLog.length > 0 && (
              <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4">
                <h3 className="mb-3 text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">Change history</h3>
                <ol className="space-y-2">
                  {changeLog.map((c, i) => {
                    const fromLabel = range(c.from);
                    const toLabel = range(c.to);
                    return (
                      <li key={i} className="rounded-lg border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-[#20242c] p-3 text-[13px]">
                        <div className="font-semibold text-gray-800 dark:text-gray-200">
                          {!fromLabel
                            ? <>Hours entered: {toLabel}</>
                            : fromLabel === toLabel
                              ? <>Approved as recorded: {toLabel}</>
                              : <>Changed <span className="font-medium text-gray-400 line-through">{fromLabel}</span> → {toLabel}</>}
                        </div>
                        {c.note && <div className="mt-1 italic text-gray-600 dark:text-gray-300">"{c.note}"</div>}
                        <div className="mt-1 text-[11px] text-gray-400">{c.by_name || 'Manager'} · {fmtLocal(c.at)}</div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}

            {/* Where the carer was at each end of the visit. Full width — the
                map sits beside the clock-in/out cards, which needs more room
                than the two-column grid above allows. */}
            <div className="mt-4 rounded-xl border border-gray-200 dark:border-gray-800 p-4">
              {/* Silent refresh — the Approve button shows its own progress, so
                  swapping the whole modal for a skeleton would be a needless flash. */}
              <VisitLocationMap
                location={data?.location}
                onApproved={() => load({ silent: true })}
              />
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 border-t border-gray-100 dark:border-gray-800 px-6 py-4">
          <div className="flex gap-2">
            <Button variant="outline" leftIcon={<Pencil className="h-4 w-4" />} onClick={() => onEditFromDay(visit.assignment_id)}>
              Edit From This Day
            </Button>
            <Button variant="danger-soft" leftIcon={<StopCircle className="h-4 w-4" />} onClick={stopFromDay}>
              Stop From This Day
            </Button>
          </div>
          <div className="flex gap-2">
            {canEditHours && !editing && (
              <Button variant="outline" onClick={() => setEditing(true)}>Edit times</Button>
            )}
            <Button variant="secondary" onClick={onClose}>Done</Button>
          </div>
        </div>
    </Modal>
  );
};

export default VisitDetailsModal;
